/**
 * Rebuilds app data field by field from an untrusted value.
 *
 * Two things reach the app from outside its own code: an export file the person
 * chooses, and whatever is sitting in this browser's IndexedDB or localStorage.
 * Neither is trusted. A file may have been hand-edited or produced by another
 * tool; stored state may have been written by an older version, corrupted, or
 * altered by anything else that can reach this origin.
 *
 * So nothing is read through: every object is reconstructed from known keys,
 * strings are length-capped, numbers are clamped to their field's range, and
 * enums are matched against the allowed set. Unknown keys are dropped, which
 * also means `__proto__` and friends never reach an assignment.
 */
import {
  ITEM_PERIODS,
  MAX_AMOUNT,
  MAX_COST_NAME,
  MAX_DISTANCE,
  MAX_ECONOMY,
  MAX_ID_LENGTH,
  MAX_IMPORT_ENTRIES,
  MAX_PRICE,
  MAX_PROFILE_NAME,
  MAX_VEHICLE_NAME,
  TIME_PERIODS,
} from "./constants";
import { costItem, makeProfile, uid } from "./calc";
import type { Currency, Data, DistanceUnit, EconomyKey, Profile, Review, Vehicle } from "./types";

export const ECONOMY_KEYS: readonly EconomyKey[] = [
  "mpg",
  "iceHomePrice",
  "iceAwayPrice",
  "evMiPerKwh",
  "evHomeRate",
  "evAwayRate",
];

/** Only plain strings survive; anything else becomes "" rather than "[object Object]". */
export const cleanText = (s: unknown, max: number): string => (typeof s === "string" ? s.slice(0, max) : "");

export const num = (n: unknown): number => {
  const x = typeof n === "number" ? n : parseFloat(String(n));
  return Number.isFinite(x) ? x : 0;
};

export const cleanNum = (n: unknown, max: number): number => Math.min(Math.max(num(n), 0), max);

/** Ids are opaque to us, so any short, safe-looking token is kept; anything else gets a fresh one. */
export const cleanId = (x: unknown): string =>
  typeof x === "string" && new RegExp(`^[A-Za-z0-9_-]{1,${MAX_ID_LENGTH}}$`).test(x) ? x : uid();

export const oneOf = <T extends string>(allowed: readonly T[], x: unknown, fallback: T): T =>
  allowed.find((a) => a === x) ?? fallback;

/** Caps how many records one file or one stored blob can create. */
export const listOf = (a: unknown): Record<string, unknown>[] =>
  (Array.isArray(a) ? a : [])
    .slice(0, MAX_IMPORT_ENTRIES)
    .map((x) => (x && typeof x === "object" ? (x as Record<string, unknown>) : {}));

export function sanitizeProfile(p: Record<string, unknown>): Profile {
  return makeProfile(
    cleanText(p.name, MAX_PROFILE_NAME),
    cleanNum(p.distanceAmount, MAX_DISTANCE),
    oneOf(TIME_PERIODS, p.distancePeriod, "year"),
    cleanNum(p.homeShare, 100),
    cleanId(p.id),
  );
}

/**
 * The "needs completing" flags. Only kept for state we wrote ourselves; an
 * imported payload has no such field, so this returns undefined for it.
 */
function sanitizeReview(r: unknown, itemIds: readonly string[]): Review | undefined {
  if (!r || typeof r !== "object") return undefined;
  const o = r as Record<string, unknown>;
  const fields = ECONOMY_KEYS.filter((k) => Array.isArray(o.fields) && o.fields.includes(k));
  const items = itemIds.filter((id) => Array.isArray(o.items) && o.items.includes(id));
  return fields.length || items.length ? { fields, items } : undefined;
}

/**
 * `keepLocalFields` is on for state this app stored and off for imported files,
 * whose payload format carries neither the review flags nor the example marker.
 */
export function sanitizeVehicle(v: Record<string, unknown>, keepLocalFields = false): Vehicle {
  const costItems = listOf(v.costItems).map((i) => {
    const item = costItem(
      cleanText(i.name, MAX_COST_NAME),
      cleanNum(i.amount, MAX_AMOUNT),
      oneOf(ITEM_PERIODS, i.period, "year"),
    );
    // Stored state keeps each item's own id, so its review flag still lines up.
    // An imported payload carries no item ids, so those get fresh ones.
    return keepLocalFields ? { ...item, id: cleanId(i.id) } : item;
  });
  const vehicle: Vehicle = {
    id: cleanId(v.id),
    name: cleanText(v.name, MAX_VEHICLE_NAME),
    fuel: v.fuel === "ev" ? "ev" : "ice",
    costItems,
    mpg: cleanNum(v.mpg, MAX_ECONOMY),
    iceHomePrice: cleanNum(v.iceHomePrice, MAX_PRICE),
    iceAwayPrice: cleanNum(v.iceAwayPrice, MAX_PRICE),
    evMiPerKwh: cleanNum(v.evMiPerKwh, MAX_ECONOMY),
    evHomeRate: cleanNum(v.evHomeRate, MAX_PRICE),
    evAwayRate: cleanNum(v.evAwayRate, MAX_PRICE),
  };
  if (!keepLocalFields) return vehicle;

  const review = sanitizeReview(
    v.review,
    costItems.map((i) => i.id),
  );
  return {
    ...vehicle,
    ...(v.isExample === true ? { isExample: true as const } : {}),
    ...(review ? { review } : {}),
  };
}

export const cleanCurrency = (c: unknown): Currency => (c === "EUR" ? "EUR" : "GBP");
export const cleanUnit = (u: unknown): DistanceUnit => (u === "km" ? "km" : "mi");

/**
 * Whole-state cleaner for what came back out of storage. Returns null when there
 * is nothing usable, which the caller treats as "start from the defaults".
 */
export function sanitizeStoredData(value: unknown): Data | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  const vehicles = listOf(o.vehicles).map((v) => sanitizeVehicle(v, true));
  const profiles = listOf(o.profiles).map(sanitizeProfile);
  if (!vehicles.length || !profiles.length) return null;
  return { vehicles, profiles, currency: cleanCurrency(o.currency), distanceUnit: cleanUnit(o.distanceUnit) };
}
